import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  MailFilled,
  MailOutlined,
} from "@ant-design/icons"
import { Button, Tag, Tooltip, Typography } from "antd"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import styled from "styled-components"

import Flex from "#blocks/Flex"
import LinkButton from "#blocks/LinkButton"
import {
  NOTIFICATION_PRIORITY,
  NOTIFICATION_SUBTYPE,
  NOTIFICATION_TYPE,
  NotificationConstructor,
} from "#customTypes/composedTypes/notificationTypes"

dayjs.extend(relativeTime)

const { Text } = Typography

const SItem = styled.div<{ $isRead: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  background: ${({ $isRead }) => ($isRead ? "transparent" : "#f6f9ff")};
  transition: background 0.15s;

  &:hover {
    background: #f0f4ff;
  }

  &:last-child {
    border-bottom: none;
  }
`

const SActions = styled.div`
  display: flex;
  gap: 2px;
  opacity: 0;
  transition: opacity 0.15s;

  ${SItem}:hover & {
    opacity: 1;
  }
`

const TYPE_LABELS: Partial<Record<NOTIFICATION_TYPE, string>> = {
  [NOTIFICATION_TYPE.EVALUATION]: "Evaluation",
  [NOTIFICATION_TYPE.CHANGELOG]: "Changelog",
  [NOTIFICATION_TYPE.PORTFOLIO]: "Portfolio",
  [NOTIFICATION_TYPE.STANDOUT]: "Standout",
}

const SUBTYPE_LABELS: Partial<Record<NOTIFICATION_SUBTYPE, string>> = {
  [NOTIFICATION_SUBTYPE.EVAL_COMMENT]: "Comment",
  [NOTIFICATION_SUBTYPE.EVAL_SUBMITTED]: "Submitted",
  [NOTIFICATION_SUBTYPE.EVAL_APPROVED]: "Approved",
  [NOTIFICATION_SUBTYPE.EVAL_REJECTED]: "Rejected",
  [NOTIFICATION_SUBTYPE.EVAL_PRINTED]: "Printed",
  [NOTIFICATION_SUBTYPE.EVAL_ASSIGNED]: "Assigned",
  [NOTIFICATION_SUBTYPE.CHANGELOG_NEW]: "New",
  [NOTIFICATION_SUBTYPE.STANDOUT_LIKED]: "Liked",
}

const PRIORITY_COLORS: Partial<Record<NOTIFICATION_PRIORITY, string>> = {
  [NOTIFICATION_PRIORITY.HIGH]: "red",
  [NOTIFICATION_PRIORITY.URGENT]: "red",
}

const SUBTYPE_TAG_CONFIG: Partial<
  Record<NOTIFICATION_SUBTYPE, { color: string; icon?: React.ReactNode }>
> = {
  [NOTIFICATION_SUBTYPE.EVAL_APPROVED]: {
    color: "success",
    icon: <CheckCircleOutlined />,
  },
  [NOTIFICATION_SUBTYPE.EVAL_REJECTED]: {
    color: "error",
    icon: <CloseCircleOutlined />,
  },
}

interface NotificationItemProps {
  notification: NotificationConstructor
  onClickBody: (notification: NotificationConstructor) => void
  onToggleRead: (notification: NotificationConstructor) => void
  onArchive: (id: string) => void
}

const NotificationItem = ({
  notification,
  onClickBody,
  onToggleRead,
  onArchive,
}: NotificationItemProps) => {
  const priorityColor = PRIORITY_COLORS[notification.priority]
  const timeAgo = dayjs(notification.createdAt).fromNow()

  return (
    <SItem
      $isRead={notification.read}
      onClick={() => onClickBody(notification)}
    >
      {!notification.read && (
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: "#1677ff",
            marginTop: 6,
            flexShrink: 0,
          }}
        />
      )}
      {notification.read && <div style={{ width: 8, flexShrink: 0 }} />}

      <Flex col grow style={{ minWidth: 0, gap: 2 }}>
        <Flex style={{ justifyContent: "space-between", alignItems: "center" }}>
          <Text strong ellipsis style={{ fontSize: 13 }}>
            {notification.header}
          </Text>
          <SActions>
            <Tooltip
              title={notification.read ? "Mark unread" : "Mark read"}
              mouseEnterDelay={0.4}
            >
              <Button
                type="text"
                size="small"
                icon={notification.read ? <MailOutlined /> : <MailFilled />}
                onClick={(e) => {
                  e.stopPropagation()
                  onToggleRead(notification)
                }}
              />
            </Tooltip>
            <Tooltip title="Archive" mouseEnterDelay={0.4}>
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                onClick={(e) => {
                  e.stopPropagation()
                  onArchive(notification.id)
                }}
              />
            </Tooltip>
          </SActions>
        </Flex>

        <Text
          type="secondary"
          ellipsis
          style={{ fontSize: 12, lineHeight: "18px" }}
        >
          {notification.message}
        </Text>

        <Flex style={{ gap: 6, alignItems: "center", marginTop: 2 }}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {timeAgo}
          </Text>
          <Tag
            color={
              (notification.subtype &&
                SUBTYPE_TAG_CONFIG[notification.subtype]?.color) ||
              "default"
            }
            icon={
              notification.subtype
                ? SUBTYPE_TAG_CONFIG[notification.subtype]?.icon
                : undefined
            }
            style={{
              fontSize: 10,
              lineHeight: "16px",
              padding: "0 4px",
              margin: 0,
            }}
          >
            {TYPE_LABELS[notification.type] ?? notification.type}
            {notification.subtype &&
              ` · ${SUBTYPE_LABELS[notification.subtype] ?? notification.subtype}`}
          </Tag>
          {priorityColor && (
            <Tag
              color={priorityColor}
              style={{
                fontSize: 10,
                lineHeight: "16px",
                padding: "0 4px",
                margin: 0,
              }}
            >
              {notification.priority}
            </Tag>
          )}
          <LinkButton style={{ fontSize: 11, padding: 0 }}>
            Click to view
          </LinkButton>
        </Flex>
      </Flex>
    </SItem>
  )
}

export default NotificationItem
